<template>
  <div>
    <slot>
      <slot
        v-for="item in items"
        name="item"
        v-bind="{
          item,
          toggleItem,
          selectItem,
          deselectItem,
          isActive: isActive(item),
        }"
      />
    </slot>
  </div>
</template>

<script lang="ts">
import type { WritableComputedRef } from 'vue'

export default defineComponent({
  name: 'MultipleSelectionList',
  props: {
    items: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    name: {
      type: String as PropType<string>,
      required: true,
    },
    modelValue: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    useDeepIsActiveCheck: {
      type: Boolean as PropType<boolean>,
      default: () => false,
    },
    initialState: {
      type: Array as PropType<any[]>,
      default: () => [],
    },
    isReset: {
      type: Boolean as PropType<boolean>,
      default: () => false,
    },
  },
  emits: ['update:modelValue'],

  setup(props, { emit }) {
    interface Item {
      key: string
      displayName: string
      count: number
      value: number
      whereCondition: { [key: string]: number }
    }

    const selected: WritableComputedRef<object[]> = computed({
      get() {
        return props.modelValue as object[]
      },
      set(newValue: object[]): void {
        emit('update:modelValue', newValue)
      },
    })

    watch(
      () => props.isReset,
      (newVal) => {
        if (newVal) {
          selected.value = [...props.modelValue]
        }
      },
      { deep: true },
    )

    const toggleItem = (item: Item) => {
      if (!isActive(item)) {
        selectItem(item)
      } else {
        deselectItem(item)
      }
    }

    const isActive = (item: Item): boolean => {
      return props.modelValue.some(
        (selectedItem: Item) => selectedItem.value === item.value,
      )
    }

    const selectItem = (item: object) => {
      selected.value = [...selected.value, item]
    }

    const deselectItem = (item: Item) => {
      const index = selected.value.findIndex(
        (i: any) => i?.value === item?.value,
      )

      if (index !== -1) {
        const newArray = [...selected.value]
        newArray.splice(index, 1)
        selected.value = newArray
      }
    }

    return {
      selected,
      isActive,
      toggleItem,
      selectItem,
      deselectItem,
    }
  },
})
</script>
